import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Divider,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Tooltip,
} from '@mui/material';
import {
    EmojiEvents as TrophyIcon,
    Speed as SpeedIcon,
    TrendingUp as TrendingUpIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { useOsobniStatistikaQuery } from '../api/JsdhNotifikaceService/service/StatistikaMutations';
import { formatujDobuReakce } from '../utils/statistikyUtils';
import type { OsobniStatistikaResponse } from '../api/JsdhNotifikaceService/generated/models/OsobniStatistika';
import UserTrendChart, { UserChartView } from './UserTrendChart';
import PeriodStatsView from './PeriodStatsView';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
);

interface StatCardProps {
    label: string;
    value: string | number;
    color?: 'success' | 'error' | 'primary' | 'secondary' | 'warning' | 'info';
    icon?: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color = 'primary', icon }) => (
    <Box
        sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: `${color}.light`,
            color: `${color}.contrastText`,
            textAlign: 'center',
        }}
    >
        {icon && <Box sx={{ mb: 1 }}>{icon}</Box>}
        <Typography variant="h5" fontWeight="bold">
            {value}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {label}
        </Typography>
    </Box>
);

// Prezentační komponenta — zobrazí statistiky pro daná data
interface StatistikaCardContentProps {
    stats: OsobniStatistikaResponse;
    title?: string;
    allowedViews?: UserChartView[];
}

export const StatistikaCardContent: React.FC<StatistikaCardContentProps> = ({ stats, title, allowedViews }) => {
    const [tabValue, setTabValue] = React.useState(0);

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {title || `Moje statistiky - ${stats.jmeno}`}
                </Typography>

                {/* Ranking and timing overview */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Tooltip title="Pořadí podle rychlosti odpovědi">
                            <Box>
                                <StatCard
                                    label="Pořadí (rychlost)"
                                    value={stats.poradiRychlost !== null ? `${stats.poradiRychlost}. / ${stats.pocetClenuJednotky}` : '-'}
                                    color="warning"
                                    icon={<TrophyIcon />}
                                />
                            </Box>
                        </Tooltip>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Tooltip title="Pořadí podle počtu odpovědí">
                            <Box>
                                <StatCard
                                    label="Pořadí (aktivita)"
                                    value={stats.poradiAktivita !== null ? `${stats.poradiAktivita}. / ${stats.pocetClenuJednotky}` : '-'}
                                    color="info"
                                    icon={<TrendingUpIcon />}
                                />
                            </Box>
                        </Tooltip>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Tooltip title="Průměrná doba vaší reakce">
                            <Box>
                                <StatCard
                                    label="Průměrná reakce"
                                    value={formatujDobuReakce(stats.prumernaDobaReakce)}
                                    color="primary"
                                    icon={<SpeedIcon />}
                                />
                            </Box>
                        </Tooltip>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Tooltip title="Průměrná doba reakce celé jednotky">
                            <Box>
                                <StatCard
                                    label="Průměr jednotky"
                                    value={formatujDobuReakce(stats.prumerDobaReakceJednotky)}
                                    color="secondary"
                                    icon={<SpeedIcon />}
                                />
                            </Box>
                        </Tooltip>
                    </Grid>
                </Grid>

                {/* Min/Max response times */}
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <CheckIcon color="success" fontSize="small" />
                                <Typography variant="body2">
                                    Nejrychlejší: <strong>{formatujDobuReakce(stats.nejrychlejsiOdpoved)}</strong>
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <CancelIcon color="error" fontSize="small" />
                                <Typography variant="body2">
                                    Nejpomalejší: <strong>{formatujDobuReakce(stats.nejpomalejsiOdpoved)}</strong>
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Period tabs */}
                <Tabs
                    value={tabValue}
                    onChange={(_, newValue) => setTabValue(newValue)}
                    variant="fullWidth"
                >
                    <Tab label="Celkem" />
                    <Tab label="Dnes" />
                    <Tab label="Týden" />
                    <Tab label="Měsíc" />
                </Tabs>

                <TabPanel value={tabValue} index={0}>
                    <PeriodStatsView stats={stats.celkem} />
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                    <PeriodStatsView stats={stats.dnes} />
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                    <PeriodStatsView stats={stats.tyden} />
                </TabPanel>
                <TabPanel value={tabValue} index={3}>
                    <PeriodStatsView stats={stats.mesic} />
                </TabPanel>
            </CardContent>

            {/* Historical charts */}
            {(stats.historieDenne || stats.historieTydenne || stats.porovnaniReakce ||
              stats.histogramReakce || stats.aktivitaPodleDne || stats.aktivitaPodleHodiny ||
              stats.trendUcasti || stats.kumulativniOdpovedi) && (
                <Box sx={{ p: 2, pt: 0 }}>
                    <UserTrendChart
                        historieDenne={stats.historieDenne}
                        historieTydenne={stats.historieTydenne}
                        porovnaniReakce={stats.porovnaniReakce}
                        histogramReakce={stats.histogramReakce}
                        aktivitaPodleDne={stats.aktivitaPodleDne}
                        aktivitaPodleHodiny={stats.aktivitaPodleHodiny}
                        trendUcasti={stats.trendUcasti}
                        kumulativniOdpovedi={stats.kumulativniOdpovedi}
                        celkemJedu={stats.celkem.pocetJedu}
                        celkemNejedu={stats.celkem.pocetNejedu}
                        allowedViews={allowedViews}
                    />
                </Box>
            )}
        </Card>
    );
};

// Wrapper s vlastním fetchem — pro tab "Moje statistiky"
const OsobniStatistikaCard: React.FC = () => {
    const { data: stats, isLoading, error } = useOsobniStatistikaQuery();

    if (isLoading) {
        return (
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent>
                    <Alert severity="error">
                        Chyba při načítání statistik: {error.message}
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    if (!stats) {
        return (
            <Card>
                <CardContent>
                    <Alert severity="info">Žádné statistiky k zobrazení.</Alert>
                </CardContent>
            </Card>
        );
    }

    return <StatistikaCardContent stats={stats} />;
};

export default OsobniStatistikaCard;
